import { Component } from '@theme/component';

const RATTLE_MS = 450;

/**
 * <tmc-blind-box>: tap to open. The box rattles, a ring of light pulses, the lineup fans out.
 * All motion is CSS keyed off data-state (closed → opening → open); this only sequences the states.
 * Without JS the lineup is always visible.
 *
 * @typedef {{ toggle: HTMLButtonElement, box: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class TmcBlindBox extends Component {
  requiredRefs = ['toggle'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.dataset.enhanced = '';
    const { toggle } = this.refs;
    toggle.hidden = false;
    toggle.addEventListener('click', () => this.#toggle(), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    this.#abort?.abort();
  }

  #toggle() {
    const { toggle } = this.refs;
    const opening = this.dataset.state === 'closed';
    clearTimeout(this.#timer);

    if (!opening) {
      this.#setState('closed');
      return;
    }

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#setState('opening');
    this.#timer = setTimeout(() => this.#setState('open'), reduced ? 0 : RATTLE_MS);
  }

  /** @param {'closed' | 'opening' | 'open'} state */
  #setState(state) {
    const { toggle } = this.refs;
    this.dataset.state = state;
    const open = state !== 'closed';
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? toggle.dataset.labelClose ?? '' : toggle.dataset.labelOpen ?? '';
  }
}

if (!customElements.get('tmc-blind-box')) customElements.define('tmc-blind-box', TmcBlindBox);
